import { useState } from 'react';
import { Button } from '@/design/components';
import { errorMessage } from '@/lib/errors';
import type { Field, ListDef } from './registry';
import type { Row } from './api';

function emptyValues(def: ListDef, row?: Row): Record<string, string> {
  return Object.fromEntries(
    def.fields.map((field) => {
      const value = row?.[field.name];
      if (field.type === 'boolean') return [field.name, value === true ? 'true' : 'false'];
      return [field.name, value === null || value === undefined ? '' : String(value)];
    }),
  );
}

/** ממיר את מה שהוקלד לטיפוסים של בסיס הנתונים. */
function toPayload(def: ListDef, values: Record<string, string>) {
  const payload: Record<string, unknown> = {};
  for (const field of def.fields) {
    const raw = values[field.name] ?? '';
    if (field.type === 'boolean') {
      payload[field.name] = raw === 'true';
    } else if (field.type === 'number') {
      payload[field.name] = raw === '' ? null : Number(raw);
    } else {
      payload[field.name] = raw === '' ? null : raw;
    }
  }
  return payload;
}

export function RowForm({
  def,
  row,
  onCancel,
  onSubmit,
}: {
  def: ListDef;
  row?: Row;
  onCancel: () => void;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
}) {
  const [values, setValues] = useState(() => emptyValues(def, row));
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const missing = def.fields.filter(
    (field) => field.required && !(values[field.name] ?? '').trim(),
  );

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSaving(true);
    try {
      await onSubmit(toPayload(def, values));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-4 rounded-md border border-line p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {def.fields.map((field) => (
          <FieldInput
            key={field.name}
            field={field}
            value={values[field.name] ?? ''}
            onChange={(next) => setValues((prev) => ({ ...prev, [field.name]: next }))}
          />
        ))}
      </div>

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={isSaving || missing.length > 0}>
          {isSaving ? 'שומר' : row ? 'שמירת השינויים' : 'הוספה'}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          ביטול
        </Button>
      </div>
    </form>
  );
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: Field;
  value: string;
  onChange: (next: string) => void;
}) {
  const id = `field-${field.name}`;
  const describedBy = field.hint ? `${id}-hint` : undefined;

  if (field.type === 'boolean') {
    return (
      <div className="grid gap-1">
        <label className="flex min-h-11 items-center gap-2 text-body">
          <input
            id={id}
            type="checkbox"
            checked={value === 'true'}
            onChange={(event) => onChange(event.target.checked ? 'true' : 'false')}
            aria-describedby={describedBy}
            className="size-5"
          />
          {field.label}
        </label>
        {field.hint && (
          <p id={describedBy} className="m-0 text-body-sm text-muted">
            {field.hint}
          </p>
        )}
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className="grid gap-1">
        <label htmlFor={id} className="text-label font-medium">
          {field.label}
          {field.required && <span aria-hidden="true"> *</span>}
        </label>
        <select
          id={id}
          value={value}
          required={field.required}
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={describedBy}
          className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
        >
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {field.hint && (
          <p id={describedBy} className="m-0 text-body-sm text-muted">
            {field.hint}
          </p>
        )}
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div className="grid gap-1">
        <label htmlFor={id} className="text-label font-medium">
          {field.label}
          {field.required && <span aria-hidden="true"> *</span>}
        </label>
        <textarea
          id={id}
          rows={4}
          value={value}
          required={field.required}
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={describedBy}
          className="min-h-11 rounded-sm border border-muted bg-surface px-3 py-2 text-body text-ink"
        />
        {field.hint && (
          <p id={describedBy} className="m-0 text-body-sm text-muted">
            {field.hint}
          </p>
        )}
      </div>
    );
  }

  const inputType =
    field.type === 'number' ? 'number' : field.type === 'time' ? 'time' : field.type === 'color' ? 'color' : 'text';

  return (
    <div className="grid gap-1">
      <label htmlFor={id} className="text-label font-medium">
        {field.label}
        {field.required && <span aria-hidden="true"> *</span>}
      </label>
      <input
        id={id}
        type={inputType}
        value={value}
        required={field.required}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={describedBy}
        dir={field.type === 'time' || field.type === 'number' ? 'ltr' : undefined}
        className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
      />
      {field.hint && (
        <p id={describedBy} className="m-0 text-body-sm text-muted">
          {field.hint}
        </p>
      )}
    </div>
  );
}
